import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  forwardRef,
  Component,
  computed,
  input,
  output,
} from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';

import { VOneIconComponent } from '@shared-components';
import { IconType } from '@shared-enums';

export type ComponentType = 'chip' | 'tag';

export type ChipVariant = 'filled' | 'stroke';

/**
 * State for tag component
 * - 'rejected': Danger/error state (red)
 * - 'warning': Warning/under review state (yellow/orange)
 * - 'approved': Success state (green)
 * - 'informative': Information state (blue)
 * - 'default': Neutral state (gray)
 * - 'positive': Success state in the lighter text tone (green)
 * - 'caution': Warning state in the lighter text tone (yellow)
 * - 'muted': Neutral state on the light gray background
 *
 * The last three carry no icon and are the tones used by inline table pills.
 */
export type TagState =
  | 'rejected'
  | 'warning'
  | 'approved'
  | 'informative'
  | 'default'
  | 'positive'
  | 'caution'
  | 'muted';

const TAG_STATES_WITH_ICON: TagState[] = [
  'rejected',
  'warning',
  'approved',
  'informative',
];

@Component({
  selector: 'v-one-chips-tags',
  imports: [NgClass, forwardRef(() => VOneIconComponent), TranslatePipe],
  templateUrl: './v-one-chips-tags.component.html',
  styleUrl: './v-one-chips-tags.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VOneChipsTagsComponent {
  protected readonly iconType = IconType;

  label = input.required<string>();

  /**
   * Type of component to render (chip or tag)
   * @default 'chip'
   */
  type = input<ComponentType>('chip');

  /**
   * Variant for chip type (filled or stroke)
   * Only applies when type='chip'
   * @default 'filled'
   */
  chipVariant = input<ChipVariant>('filled');

  /**
   * State for tag type
   * Only applies when type='tag'
   * @default 'default'
   */
  tagState = input<TagState>('default');

  /**
   * Whether chip is closeable
   * Only applies when type='chip'
   * @default false
   */
  closeable = input<boolean>(false);

  /**
   * Whether to show icon in tag
   * Only applies when type='tag'
   * @default true
   */
  showIcon = input<boolean>(true);

  /**
   * Whether component is disabled
   * Only applies when type='chip'
   * @default false
   */
  disabled = input<boolean>(false);

  /**
   * Renders a smaller, bolder variant (e.g. inline status indicators)
   * @default false
   */
  compact = input<boolean>(false);

  closed = output<void>();

  clicked = output<void>();

  componentClasses = computed(() => {
    const classes: string[] = [];
    const componentType = this.type();

    if (componentType === 'chip') {
      classes.push('chips-tags--chip');
      classes.push(`chips-tags--chip-${this.chipVariant()}`);
      if (this.disabled()) {
        classes.push('chips-tags--disabled');
      }
      if (this.closeable()) {
        classes.push('chips-tags--closeable');
      }
    } else {
      classes.push('chips-tags--tag');
      classes.push(`chips-tags--tag-${this.tagState()}`);
    }

    if (this.compact()) {
      classes.push('chips-tags--compact');
    }

    return classes;
  });

  /**
   * Determines if component is a chip
   */
  isChip = computed(() => this.type() === 'chip');

  /**
   * Determines if component is a tag
   */
  isTag = computed(() => this.type() === 'tag');

  shouldShowIcon = computed(() => {
    return (
      this.isTag() &&
      this.showIcon() &&
      TAG_STATES_WITH_ICON.includes(this.tagState())
    );
  });

  /**
   * Handle close button click
   * @param event - Click event
   */
  onClose(event: Event): void {
    event.stopPropagation();
    if (!this.disabled()) {
      this.closed.emit();
    }
  }

  onClick(): void {
    if (this.isChip() && !this.disabled()) {
      this.clicked.emit();
    }
  }
}
